import React, { FC, useEffect, useReducer, Reducer } from 'react';
import PropTypes from 'prop-types';
import { fetchUploadUrls, uploadFile, readFile, isFileValid } from './imageUploadApi';
import { getCroppedImg } from './cropImage';
import { ImageUploadState, Action, CroppedImageInfo, Area, ACTION_TYPE, UploadPOSTUrls } from './imageUploadTypes';
import { reducer } from './imageUploadHelpers';
import { FileDropzone, ProgressBar, ImageCropper } from '../index';
import './style.scss';

const initialCropAreaPixels: Area = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

const initialCroppedImageInfo: CroppedImageInfo = {
  croppedImage: null,
  croppedImageUrl: '',
};

const initialUploadPOSTUrls: UploadPOSTUrls = {
  downloadUrl: '',
  uploadUrl: '',
  filePath: '',
};

const initialState: ImageUploadState = {
  file: undefined,
  fileSrc: '',
  isUploading: false,
  isUploadFinished: false,
  uploadProgress: 0,
  cropAreaPixels: initialCropAreaPixels,
  croppedImageInfo: initialCroppedImageInfo,
  uploadUrls: initialUploadPOSTUrls,
  imageDeleted: false,
  error: '',
};

interface ImageUploadProps {
  customerId: string;
  label?: string;
  imageUrl?: string;
  onFilePathChanged?: (filePath?: string) => void;
  onImageCropped?: () => void;
  onCancel?: () => void;
}

const ImageUpload: FC<ImageUploadProps> = ({
  customerId,
  label,
  imageUrl,
  onFilePathChanged,
  onImageCropped,
  onCancel,
}) => {
  const [state, dispatch] = useReducer<Reducer<ImageUploadState, Action>>(reducer, initialState);

  useEffect(() => {
    // Don't go get a signed url if imageUrl is passed in
    if (!imageUrl) {
      fetchUploadUrls(customerId, 'jpg').then((uploadUrls) =>
        dispatch({ type: ACTION_TYPE.UPLOAD_URLS, payload: uploadUrls })
      );
    }
  }, []);

  const renderProgress = (): JSX.Element => {
    return (
      <div>
        <ProgressBar progressUpdate={state.uploadProgress} />
      </div>
    );
  };

  const onUploaded = (): void => {
    if (onFilePathChanged) {
      onFilePathChanged(state.uploadUrls.downloadUrl);
    }
  };

  const handleFilesAdded = async (files: File[]): Promise<void> => {
    dispatch({ type: ACTION_TYPE.ERROR, payload: '' });

    try {
      // only grab the first file
      const file = files[0];
      if (isFileValid(file)) {
        dispatch({ type: ACTION_TYPE.FILE, payload: file });
        await readFile(file).then((src) => {
          dispatch({ type: ACTION_TYPE.FILE_SRC, payload: src });
          if (onImageCropped) {
            onImageCropped();
          }
        });
      }
    } catch (error) {
      console.log(error);
      dispatch({ type: ACTION_TYPE.ERROR, payload: error?.message });
    }
  };

  const handleImageDelete = (): void => {
    dispatch({ type: ACTION_TYPE.IMAGE_DELETED, payload: true });
    dispatch({ type: ACTION_TYPE.FILE, payload: undefined });
    dispatch({ type: ACTION_TYPE.CROPPED_IMAGE_INFO, payload: initialCroppedImageInfo });
    const element: HTMLElement | null = document.getElementById('image-url');
    if (element) {
      element.setAttribute('value', '');
    }
    if (onFilePathChanged) {
      onFilePathChanged();
    }
    if (onCancel) {
      onCancel();
    }
  };

  const handleFileUpload = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.stopPropagation();

    getCroppedImg(state.fileSrc, state.cropAreaPixels).then((croppedImageInfo: CroppedImageInfo) => {
      dispatch({ type: ACTION_TYPE.CROPPED_IMAGE_INFO, payload: croppedImageInfo });

      if (croppedImageInfo.croppedImage) {
        uploadFile({
          file: croppedImageInfo.croppedImage,
          uploadUrl: state.uploadUrls.uploadUrl,
          dispatch,
        })
          .then(() => {
            dispatch({ type: ACTION_TYPE.UPLOAD_FINISHED, payload: true });
            onUploaded();
          })
          .catch((error: any) => {
            console.log(error);
            dispatch({ type: ACTION_TYPE.ERROR, payload: error?.message });
            handleImageDelete();
          })
          .finally(() => dispatch({ type: ACTION_TYPE.IS_UPLOADING, payload: false }));
      }
    });
  };

  return (
    <div>
      {imageUrl ? (
        <div>
          <img className="la1-imageupload-thumbnail" src={imageUrl} alt="custom"></img>
        </div>
      ) : (
        <>
          {!state.file && (
            <div>
              <FileDropzone
                disabled={state.isUploading}
                label={label}
                onFilesAdded={handleFilesAdded}
                accept="image/jpeg, image/png"
              />
            </div>
          )}
          {state.croppedImageInfo.croppedImage && (
            <div>
              <img
                className="la1-imageupload-thumbnail"
                src={state.croppedImageInfo.croppedImageUrl}
                alt="custom"
              ></img>
            </div>
          )}
          {state.file && !state.croppedImageInfo.croppedImageUrl && (
            <div>
              <div className="la1-imageupload-crop-container">
                <ImageCropper
                  fileSrc={state.fileSrc}
                  aspect={16 / 9}
                  onCropComplete={(cropArea): void =>
                    dispatch({ type: ACTION_TYPE.CROP_AREA_PIXELS, payload: cropArea })
                  }
                />
              </div>
              <button className="btn btn-primary" style={{ marginRight: '5px' }} onClick={handleFileUpload}>
                Upload
              </button>
              <button className="btn btn-default" onClick={handleImageDelete}>
                Cancel
              </button>
            </div>
          )}
          <div>
            {(state.file && state.isUploading) ||
              (state.isUploadFinished && (
                <div key={state.file?.name} className="la1-imageupload-row">
                  <span className="la1-imageupload-filename">Name: {state.file?.name}</span>
                  {renderProgress()}
                </div>
              ))}
          </div>
          {state.error && (
            <div className="la1-imageupload-err-msg">
              <i className="fa fa-exclamation-circle" />
              {state.error}
            </div>
          )}
        </>
      )}
    </div>
  );
};

ImageUpload.propTypes = {
  customerId: PropTypes.string.isRequired,
  label: PropTypes.string,
  imageUrl: PropTypes.string,
  onFilePathChanged: PropTypes.func,
  onImageCropped: PropTypes.func,
  onCancel: PropTypes.func,
};

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
